import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { FILTERS } from '../configs/filters/filters';
import {
  ACTIVE_SORT_HTTP_PARAM_NAMES,
  ActiveFilters,
  ActiveSort,
  CheckboxValueType,
  CheckboxFilterType,
  FilterMenuConfig,
  FilterSortDirection,
  FilterType,
  FilterTypeName,
  RangeFilterType,
  RadioFilterType,
} from '../types/filter.type';
import { getActiveFilters } from '../utils/filters-utils';
import { ParamMap } from '@angular/router';
import { getFilterParamName, getRangeParamNames } from '../utils/filters-query-params';

@Injectable({
  providedIn: 'root',
})
export class FilterService {
  filter: WritableSignal<Record<string, FilterMenuConfig>> = signal(FILTERS);
  activeSort: WritableSignal<ActiveSort | undefined> = signal(undefined);
  activeFilters: Signal<ActiveFilters> = computed(() => getActiveFilters(this.filter()));
  private dynamicFilterConfigs: Record<string, FilterMenuConfig> = {};
  private readonly pendingValues: WritableSignal<Record<string, string[]>> = signal<
    Record<string, string[]>
  >({});
  readonly pendingActiveValues: Signal<Record<string, string[]>> = this.pendingValues.asReadonly();

  hasActiveFilter(column: string): boolean {
    return (
      Object.keys(
        getActiveFilters(this.filter(), {
          tableHeaderElements: [column],
        }),
      ).length > 0
    );
  }

  setFilterValue<T extends FilterTypeName>(
    column: string,
    filterIndex: number,
    type: T,
    patchValue: Partial<Omit<Extract<FilterType, { type: T }>, 'type'>>,
  ): void {
    this.filter.update((filters: Record<string, FilterMenuConfig>) => ({
      ...filters,
      [column]: {
        ...filters[column],
        filters: filters[column].filters.map((filter: FilterType, index: number) => {
          if (index !== filterIndex || filter.type !== type) return filter;

          return {
            ...filter,
            ...patchValue,
          };
        }),
      },
    }));
  }

  setRangeValue(
    column: string,
    filterIndex: number,
    key: keyof Pick<RangeFilterType, 'min' | 'max'>,
    value: number | undefined,
  ): void {
    this.setFilterValue(column, filterIndex, 'range', { [key]: value });
  }

  setValue(column: string, filterIndex: number, value: string | number): void {
    this.setFilterValue(column, filterIndex, 'search', { value });
  }

  setFilterSortDirection(column: string, sortDirection: FilterSortDirection): void {
    this.activeSort.set({
      sortType: column,
      sortDirection,
    });
  }

  getActiveSortDirection(column: string): FilterSortDirection | undefined {
    const activeSort: ActiveSort | undefined = this.activeSort();

    return activeSort?.sortType === column ? activeSort.sortDirection : undefined;
  }

  toggleFilterSortDirection(column: string): void {
    const activeSortDirection: FilterSortDirection | undefined =
      this.getActiveSortDirection(column);

    if (!activeSortDirection) {
      this.setFilterSortDirection(column, 'asc');
      return;
    }

    if (activeSortDirection === 'asc') {
      this.setFilterSortDirection(column, 'desc');
      return;
    }

    this.activeSort.set(undefined);
  }

  setCheckboxValue(
    column: string,
    filterIndex: number,
    valueIndex: number,
    isActive: boolean,
  ): void {
    const filter: FilterType | undefined = this.filter()[column]?.filters[filterIndex];
    if (filter?.type !== 'checkbox') return;

    this.setFilterValue(column, filterIndex, 'checkbox', {
      data: filter.data.map((data: CheckboxValueType, checkboxIndex: number) => {
        if (checkboxIndex !== valueIndex) return data;

        return {
          ...data,
          isActive,
        };
      }),
    });
  }

  setRadioValue(column: string, filterIndex: number, valueIndex: number, isActive: boolean): void {
    const filter: FilterType | undefined = this.filter()[column]?.filters[filterIndex];
    if (filter?.type !== 'radio') return;

    this.setFilterValue(column, filterIndex, 'radio', {
      data: filter.data.map((data: CheckboxValueType, radioIndex: number) => ({
        ...data,
        isActive: radioIndex === valueIndex ? isActive : false,
      })),
    });
  }

  setFilterData(column: string, filterIndex: number, data: CheckboxValueType[]): void {
    const filter: FilterType | undefined = this.filter()[column]?.filters[filterIndex];
    if (filter?.type !== 'checkbox' && filter?.type !== 'radio') return;

    const pendingValues: string[] | undefined = this.pendingValues()[column];
    this.clearPendingValues(column);

    const activeValues: Set<string> = new Set(
      pendingValues ??
        filter.data
          .filter((value: CheckboxValueType) => value.isActive === true)
          .map((value: CheckboxValueType) => value.value),
    );
    const nextData: CheckboxValueType[] = data.map((value: CheckboxValueType) => ({
      ...value,
      isActive: activeValues.has(value.value),
    }));
    if (this.areFilterDataEqual(filter.data, nextData)) return;

    this.setFilterValue(column, filterIndex, filter.type, { data: nextData });
    this.dynamicFilterConfigs[column] = this.buildDynamicFilterConfig(
      column,
      filterIndex,
      nextData,
    );
  }

  /** Restores the filters and the sort previously serialized in the URL. */
  applyQueryParams(queryParams: ParamMap): void {
    Object.entries(this.filter()).forEach(([column, config]: [string, FilterMenuConfig]) => {
      config.filters.forEach((filter: FilterType, index: number) =>
        this.applyFilterQueryParams(queryParams, column, index, filter),
      );
    });

    this.applySortQueryParams(queryParams);
  }

  setActiveValues(column: string, filterIndex: number, values: string[]): void {
    const filter: FilterType | undefined = this.filter()[column]?.filters[filterIndex];
    if (filter?.type !== 'checkbox' && filter?.type !== 'radio') return;

    this.pendingValues.update((current: Record<string, string[]>) => ({
      ...current,
      [column]: values,
    }));

    this.setFilterValue(column, filterIndex, filter.type, {
      data: filter.data.map((value: CheckboxValueType) => ({
        ...value,
        isActive: values.includes(value.value),
      })),
    });
  }

  resetFilter(column?: string): void {
    if (!column) {
      this.pendingValues.set({});
      this.filter.set({
        ...FILTERS,
        ...this.dynamicFilterConfigs,
      });
      this.activeSort.set(undefined);
      return;
    }

    this.clearPendingValues(column);

    if (this.activeSort()?.sortType === column) {
      this.activeSort.set(undefined);
    }

    const defaultFilterConfig: FilterMenuConfig | undefined =
      this.dynamicFilterConfigs[column] ?? FILTERS[column];
    if (!defaultFilterConfig) return;

    this.filter.update((filters: Record<string, FilterMenuConfig>) => ({
      ...filters,
      [column]: {
        ...defaultFilterConfig,
        filters: defaultFilterConfig.filters.map((filter: FilterType) => ({
          ...filter,
          ...(filter.type === 'checkbox' || filter.type === 'radio'
            ? {
                data: filter.data.map((data: CheckboxValueType) => ({ ...data })),
              }
            : {}),
        })),
      },
    }));
  }

  private clearPendingValues(column: string): void {
    if (!(column in this.pendingValues())) return;

    this.pendingValues.update((current: Record<string, string[]>) => {
      const { [column]: _removed, ...rest } = current;

      return rest;
    });
  }

  private applyFilterQueryParams(
    queryParams: ParamMap,
    column: string,
    filterIndex: number,
    filter: FilterType,
  ): void {
    const paramName: string = getFilterParamName(column, filterIndex);

    switch (filter.type) {
      case 'checkbox':
      case 'radio': {
        const values: string[] = queryParams.getAll(paramName);
        if (values.length > 0) this.setActiveValues(column, filterIndex, values);
        return;
      }
      case 'range': {
        const { min, max } = getRangeParamNames(paramName);
        this.applyRangeQueryParam(queryParams, column, filterIndex, 'min', min);
        this.applyRangeQueryParam(queryParams, column, filterIndex, 'max', max);
        return;
      }
      case 'search': {
        const value: string | null = queryParams.get(paramName);
        if (value) this.setValue(column, filterIndex, value);
        return;
      }
    }
  }

  private applyRangeQueryParam(
    queryParams: ParamMap,
    column: string,
    filterIndex: number,
    key: keyof Pick<RangeFilterType, 'min' | 'max'>,
    paramName: string,
  ): void {
    const rawValue: string | null = queryParams.get(paramName);
    if (rawValue === null) return;

    const value: number = Number(rawValue);
    if (Number.isNaN(value)) return;

    this.setRangeValue(column, filterIndex, key, value);
  }

  private applySortQueryParams(queryParams: ParamMap): void {
    const sortType: string | null = queryParams.get(ACTIVE_SORT_HTTP_PARAM_NAMES.sortType);
    const sortDirection: string | null = queryParams.get(
      ACTIVE_SORT_HTTP_PARAM_NAMES.sortDirection,
    );

    if (!sortType || (sortDirection !== 'asc' && sortDirection !== 'desc')) return;

    this.setFilterSortDirection(sortType, sortDirection);
  }

  private buildDynamicFilterConfig(
    column: string,
    filterIndex: number,
    data: CheckboxValueType[],
  ): FilterMenuConfig {
    const filterConfig: FilterMenuConfig = this.filter()[column];

    return {
      ...filterConfig,
      filters: filterConfig.filters.map((filter: FilterType, index: number) => {
        if (index !== filterIndex || (filter.type !== 'checkbox' && filter.type !== 'radio')) {
          return filter;
        }

        return {
          ...filter,
          data: data.map((value: CheckboxValueType) => ({
            ...value,
            isActive: false,
          })),
        } as CheckboxFilterType | RadioFilterType;
      }),
    };
  }

  private areFilterDataEqual(
    currentData: CheckboxValueType[],
    nextData: CheckboxValueType[],
  ): boolean {
    if (currentData.length !== nextData.length) return false;

    return currentData.every((currentValue: CheckboxValueType, index: number) => {
      const nextValue: CheckboxValueType = nextData[index];

      return (
        currentValue.label === nextValue.label &&
        currentValue.value === nextValue.value &&
        currentValue.icon === nextValue.icon &&
        currentValue.iconUrl === nextValue.iconUrl &&
        currentValue.isActive === nextValue.isActive
      );
    });
  }
}
